import { Image, useColorMode } from '@chakra-ui/react';
import logoLockup from '../../assets/brand/logo-lockup.svg';
import logoLockupDark from '../../assets/brand/logo-lockup-dark.svg';
import logoMark from '../../assets/brand/logo-mark.svg';

interface LogoProps {
  /**
   * Height of the logo. Width is auto, preserving the aspect ratio.
   * @default "32px"
   */
  size?: string;
  /**
   * `lockup` (default) is the mark + wordmark. `mark` is the symbol alone —
   * use it where the word "Bower" is already on screen, or below ~24px where
   * the wordmark stops being legible.
   */
  variant?: 'lockup' | 'mark';
  /**
   * Additional CSS class names
   */
  className?: string;
}

/**
 * The Bower logo. Single source of truth — every surface renders through this,
 * so a mark change is one import, not a grep.
 *
 * 2026-08 identity: the sprig mark plus the GT Ultra wordmark, as SVG rather
 * than the previous PNG pair. SVG matters more than usual here — the mark is a
 * Twilight→Bluebird gradient (#5C88DA → #69B3E7) over fine tapered strokes,
 * and those strokes visibly alias at small raster sizes.
 *
 * Only the WORDMARK changes between colour modes (near-black vs parchment);
 * the mark's gradient is legible on both, which is why `variant="mark"` needs
 * no dark variant.
 *
 * The SVGs are copied verbatim from the public website (bowerlabs.ai
 * `/assets/`), so the app and the site show the same mark. Re-copy from there
 * rather than re-exporting from design.
 *
 * Loaded via `src`, not inlined: the SVGs carry generic exported gradient ids
 * (`paint0_linear_…`), so inlining two into one document would let whichever
 * mounted last capture the other's fill.
 *
 * ```tsx
 * <Logo size="24px" />
 * <Logo size="40px" variant="mark" />
 * ```
 */
export const Logo: React.FC<LogoProps> = ({ size = '32px', variant = 'lockup', className }) => {
  const { colorMode } = useColorMode();
  const lockupSrc = colorMode === 'dark' ? logoLockupDark : logoLockup;
  const src = variant === 'mark' ? logoMark : lockupSrc;

  return <Image src={src} alt="Bower" h={size} w="auto" className={className} />;
};
